/* ─────────────────────────────────────────────────────────────────────────
 * Cutout — the mood catalogue.
 *
 * Five moods. Each is ONE WORD (the thing you cut out), a paper colour, a
 * grade the photographs are printed through, and ten frames grouped into
 * three named projects. The word's letter count matters: it decides how many
 * frames can develop INSIDE the type before the rest of the roll arrives on
 * the contact sheet, so the words were chosen to span 3 → 9 letters.
 * ───────────────────────────────────────────────────────────────────────── */

export interface CutoutFrame {
  /** File under /assets/sections/cutout/ */
  src: string;
  title: string;
  place: string;
  year: string;
  /** Index into `Mood.projects`. */
  project: number;
  /** Grease-pencil "select" — circled on the sheet the way a real editor
   *  rings the keepers. Two per mood, never more; a sheet where everything
   *  is circled says nothing. */
  select?: boolean;
}

export interface CutoutProject {
  name: string;
  blurb: string;
}

/** Cheap canvas grade — applied when a photograph is printed, so each mood's
 *  frames read as one body of work rather than a folder of stock. `filter` is
 *  a canvas2d filter string (skipped where unsupported), the rest are the
 *  hand-rolled veils that run everywhere. */
export interface CutoutGrade {
  filter: string;
  /** rgba veil multiplied over the print — the mood's cast. */
  cast: string;
  castAlpha: number;
  /** Extra grain on top of the section-wide grain. 0–1. */
  grain: number;
  /** Corner darkening. 0–1. */
  vignette: number;
}

export interface CutoutMood {
  id: string;
  /** The cut-out word. Uppercase — these are the glyphs the photos fill. */
  word: string;
  /** Sits under the word on the mat, in pencil. */
  note: string;
  /** The paper the word is cut from. */
  paper: string;
  /** The ink the word is printed in, before the photographs arrive. */
  ink: string;
  /** Grease pencil / annotation colour on this mood's sheet. */
  pencil: string;
  /** The film the whole body of work is shot on — printed on the sheet edge. */
  stock: string;
  /** One line, the photographer's own words. Reads on the contact sheet. */
  statement: string;
  grade: CutoutGrade;
  projects: CutoutProject[];
  frames: CutoutFrame[];
}

const F = (
  src: string,
  title: string,
  place: string,
  year: string,
  project: number,
  select?: boolean
): CutoutFrame => ({ src, title, place, year, project, select });

export const MOODS: CutoutMood[] = [
  {
    id: "bold",
    word: "BOLD",
    note: "colour that argues",
    paper: "#DE3B22",
    ink: "#FFF3E4",
    pencil: "#FFE66B",
    stock: "Ektachrome E100",
    statement:
      "Nothing is muted down. If the wall is red I let it be red at you.",
    grade: {
      filter: "saturate(1.24) contrast(1.12)",
      cast: "rgba(255,74,32,0.10)",
      castAlpha: 1,
      grain: 0.28,
      vignette: 0.2,
    },
    projects: [
      { name: "Quarry Day", blurb: "Two suits, one quarry, full sun at noon." },
      { name: "Argument", blurb: "A studio month spent arguing with colour." },
      { name: "Street Signal", blurb: "Cities where the paint does the shouting." },
    ],
    frames: [
      F("bold-01.jpg", "Verde on Red Rock", "Almería", "2025", 0, true),
      F("bold-02.jpg", "Scarlet, Full Sun", "Almería", "2025", 0),
      F("bold-03.jpg", "Gel Test 04", "Studio B", "2024", 1),
      F("bold-04.jpg", "Bloom", "Studio B", "2024", 1),
      F("bold-05.jpg", "Redline", "Seoul", "2024", 1, true),
      F("bold-06.jpg", "Court Blue", "Chicago", "2023", 2),
      F("bold-07.jpg", "No Parking", "Tokyo", "2024", 2),
      F("bold-08.jpg", "Static", "Lisbon", "2025", 2),
      F("bold-09.jpg", "Zebra, Noon", "Mexico City", "2023", 2),
      F("bold-10.jpg", "Flare Study", "Studio B", "2024", 1),
    ],
  },
  {
    id: "soft",
    word: "SOFT",
    note: "one window, no flash",
    paper: "#EFE3D2",
    ink: "#33291F",
    pencil: "#B4795A",
    stock: "Portra 160",
    statement:
      "A north window, a long lens, and the patience to wait for the cloud.",
    grade: {
      filter: "saturate(0.86) contrast(0.92) brightness(1.06)",
      cast: "rgba(255,236,214,0.14)",
      castAlpha: 1,
      grain: 0.14,
      vignette: 0.08,
    },
    projects: [
      { name: "Object Studies", blurb: "Things photographed until they hold still." },
      { name: "End of Afternoon", blurb: "Interiors, once the sun is off the wall." },
      { name: "The Inch Before", blurb: "Hands, and the moment before contact." },
    ],
    frames: [
      F("soft-01.jpg", "Object Study 01", "Studio A", "2025", 0),
      F("soft-02.jpg", "Paper Weights", "Studio A", "2025", 0),
      F("soft-03.jpg", "Low Flame", "Copenhagen", "2024", 1, true),
      F("soft-04.jpg", "Stoneware", "Studio A", "2025", 0),
      F("soft-05.jpg", "Steam, Green Glass", "Studio A", "2024", 0, true),
      F("soft-06.jpg", "Vessel", "Studio A", "2024", 0),
      F("soft-07.jpg", "One Chair", "Milan", "2024", 1),
      F("soft-08.jpg", "Almost Touching", "Studio A", "2025", 2),
      F("soft-09.jpg", "Nightlights", "Copenhagen", "2024", 1),
      F("soft-10.jpg", "Moon Lamp", "Studio A", "2025", 1),
    ],
  },
  {
    id: "raw",
    word: "RAW",
    note: "push it two stops",
    paper: "#191715",
    ink: "#F4F1E8",
    pencil: "#FF5B39",
    stock: "HP5 PLUS @ 1600",
    statement:
      "Available light, whatever it is. I would rather have the grain than the tripod.",
    grade: {
      filter: "grayscale(1) contrast(1.3) brightness(0.96)",
      cast: "rgba(20,22,26,0.10)",
      castAlpha: 1,
      grain: 0.62,
      vignette: 0.3,
    },
    projects: [
      { name: "Six A.M.", blurb: "The hour the city belongs to nobody." },
      { name: "Transit", blurb: "Platforms, underpasses, the space between." },
      { name: "Too Fast", blurb: "Frames where the subject beat the shutter." },
    ],
    frames: [
      F("raw-01.jpg", "Wet Street, 6am", "New York", "2023", 0, true),
      F("raw-02.jpg", "Crossing", "New York", "2023", 0),
      F("raw-03.jpg", "Platform", "Berlin", "2022", 1),
      F("raw-04.jpg", "Runner", "Lisbon", "2023", 0),
      F("raw-05.jpg", "Grid at Night", "Tokyo", "2023", 1),
      F("raw-06.jpg", "Rain, Shibuya", "Tokyo", "2023", 0, true),
      F("raw-07.jpg", "Underpass", "Berlin", "2022", 1),
      F("raw-08.jpg", "Crowd Blur", "Paris", "2024", 2),
      F("raw-09.jpg", "Missed Him", "Paris", "2024", 2),
      F("raw-10.jpg", "Still, While Everything Moves", "Paris", "2024", 2),
    ],
  },
  {
    id: "dreamy",
    word: "DREAMY",
    note: "shoot it through glass",
    paper: "#BFC7EA",
    ink: "#262A45",
    pencil: "#E86BA0",
    stock: "CineStill 800T",
    statement:
      "Half of these were made by putting something in front of the lens on purpose.",
    grade: {
      filter: "saturate(1.06) contrast(0.9) brightness(1.05)",
      cast: "rgba(168,150,255,0.16)",
      castAlpha: 1,
      grain: 0.2,
      vignette: 0.14,
    },
    projects: [
      { name: "Long Exposure", blurb: "Fields at night, shutter held open." },
      { name: "Through Glass", blurb: "Prisms, water, a broken filter I kept." },
      { name: "After Sundown", blurb: "The twenty minutes once the sun has gone." },
    ],
    frames: [
      F("dreamy-01.jpg", "Lavender, Long Exposure", "Provence", "2024", 0, true),
      F("dreamy-02.jpg", "Reach", "Studio C", "2024", 1),
      F("dreamy-03.jpg", "Firefly Spiral", "Provence", "2024", 0),
      F("dreamy-04.jpg", "Two Hands, One Beam", "Studio C", "2024", 1),
      F("dreamy-05.jpg", "Cloudbreak", "Azores", "2023", 0),
      F("dreamy-06.jpg", "Golden Hour Crowd", "Lisbon", "2024", 2),
      F("dreamy-07.jpg", "Glow Test", "Studio C", "2024", 1),
      F("dreamy-08.jpg", "Halo", "Azores", "2023", 0, true),
      F("dreamy-09.jpg", "Mist Walk", "Faroe Islands", "2023", 2),
      F("dreamy-10.jpg", "Violet Hour", "Studio C", "2025", 2),
    ],
  },
  {
    id: "cinematic",
    word: "CINEMATIC",
    note: "one lamp, one window",
    paper: "#EFB61F",
    ink: "#241C10",
    /* chinagraph red — the pencil has to read on the print AND on the
     * cream of the sheet, which rules out anything near the ink */
    pencil: "#CC3A22",
    stock: "Vision3 500T",
    statement:
      "I light a person the way a room would have lit them, then take the room away.",
    grade: {
      filter: "saturate(1.02) contrast(1.16) brightness(0.97)",
      cast: "rgba(24,42,64,0.16)",
      castAlpha: 1,
      grain: 0.3,
      vignette: 0.34,
    },
    projects: [
      { name: "One Practical", blurb: "Rooms in Rome, lit by whatever was in them." },
      { name: "Through Slats", blurb: "Athens in August, shot through shutters." },
      { name: "After Service", blurb: "Hong Kong, once the kitchen had closed." },
    ],
    frames: [
      F("cinematic-01.jpg", "Interior, Night", "Rome", "2024", 0, true),
      F("cinematic-02.jpg", "One Lamp", "Rome", "2024", 0),
      F("cinematic-03.jpg", "Blinds", "Athens", "2023", 1),
      F("cinematic-04.jpg", "Dapple", "Athens", "2023", 1),
      F("cinematic-05.jpg", "Amber Profile", "Rome", "2024", 0),
      F("cinematic-06.jpg", "Poster Wall", "Hong Kong", "2023", 2),
      F("cinematic-07.jpg", "Last Light", "Hong Kong", "2023", 2, true),
      F("cinematic-08.jpg", "Neon Diner", "Hong Kong", "2023", 2),
      F("cinematic-09.jpg", "Turning", "Rome", "2024", 0),
      F("cinematic-10.jpg", "Doorway", "Marrakech", "2025", 1),
    ],
  },
];

/* THE SAME URLS THE SPEC SELLS. The files also exist under
 * `public/assets/sections/cutout/` and that is where they are edited — but
 * the section loads the CDN copies, so what a visitor sees and what a buyer's
 * `IMG_BASE` resolves to are provably one set of bytes. Split the two and the
 * demo drifts from the product the moment either side is touched.
 *
 * Plain <img> / Image() hot-links: nothing here reads a photograph back off a
 * canvas, so no CORS grant is needed. Served immutable with a year's cache. */
export const IMG_BASE =
  "https://pub-dc2abe5caae44240a6d1913566a75e0f.r2.dev/sections/cutout/";

/** The photographer whose desk this is. */
export const STUDIO = {
  name: "MAREN OKONKWO",
  role: "Photographer",
  city: "Lisbon / anywhere",
  mail: "hello@marenokonkwo.com",
};
